// DEPLOY: /var/www/woody.wtf/frontend/src/chart/indicators.js
// woody.wtf chart engine: indicator library. Everything is calculated here, in the browser,
// from the candles on screen (split-adjusted, current timeframe) - nothing comes from the DB.
// Each indicator: { name, short, pane: 'price' | 'own', params: [{k, label, def, min?, step?, source?}],
//   outputs: [{k, label, kind: 'line'|'hist'|'dots', color}], fills?: [[outA, outB, colour]],
//   range?: [min, max] (fixed scale for its own pane), levels?: [values], calc(bars, p) -> {outputKey: array} }
// Output arrays line up with the candles (NaN = no value); they may run past the last candle
// (e.g. Ichimoku cloud shifted forward) into the blank future area.

const N = NaN;
const SOURCES = ['close', 'open', 'high', 'low', 'hl2', 'hlc3', 'ohlc4'];

export function src(bars, s) {
  return bars.map(b => (s === 'open' ? b.o : s === 'high' ? b.h : s === 'low' ? b.l
    : s === 'hl2' ? (b.h + b.l) / 2 : s === 'hlc3' ? (b.h + b.l + b.c) / 3
    : s === 'ohlc4' ? (b.o + b.h + b.l + b.c) / 4 : b.c));
}

// ── Building blocks (NaN-tolerant: a series starts once enough real values exist) ──
export function sma(x, n) {
  const out = new Array(x.length).fill(N);
  let sum = 0, cnt = 0;
  for (let i = 0; i < x.length; i++) {
    if (!Number.isNaN(x[i])) { sum += x[i]; cnt++; }
    if (i >= n && !Number.isNaN(x[i - n])) { sum -= x[i - n]; cnt--; }
    if (cnt === n) out[i] = sum / n;
  }
  return out;
}

// EMA seeded with the SMA of its first n values.
export function ema(x, n, alpha = 2 / (n + 1)) {
  const out = new Array(x.length).fill(N);
  let prev = N, seed = 0, cnt = 0;
  for (let i = 0; i < x.length; i++) {
    const v = x[i];
    if (Number.isNaN(v)) { if (!Number.isNaN(prev)) out[i] = prev; continue; }
    if (Number.isNaN(prev)) {
      seed += v; cnt++;
      if (cnt === n) { prev = seed / n; out[i] = prev; }
    } else { prev = prev + alpha * (v - prev); out[i] = prev; }
  }
  return out;
}

const rma = (x, n) => ema(x, n, 1 / n);   // Wilder smoothing

export function wma(x, n) {
  const out = new Array(x.length).fill(N), den = n * (n + 1) / 2;
  for (let i = n - 1; i < x.length; i++) {
    let s = 0, ok = true;
    for (let k = 0; k < n; k++) { const v = x[i - k]; if (Number.isNaN(v)) { ok = false; break; } s += v * (n - k); }
    if (ok) out[i] = s / den;
  }
  return out;
}

function stdev(x, n, mean = sma(x, n)) {
  return x.map((_, i) => {
    if (Number.isNaN(mean[i])) return N;
    let s = 0;
    for (let k = 0; k < n; k++) s += (x[i - k] - mean[i]) ** 2;
    return Math.sqrt(s / n);
  });
}

const rolling = (x, n, f) => x.map((_, i) => (i < n - 1 ? N : f(x.slice(i - n + 1, i + 1))));
const highest = (x, n) => rolling(x, n, w => Math.max(...w));
const lowest = (x, n) => rolling(x, n, w => Math.min(...w));
const sub = (a, b) => a.map((v, i) => v - b[i]);

function trueRange(bars) {
  return bars.map((b, i) => (i === 0 ? b.h - b.l
    : Math.max(b.h - b.l, Math.abs(b.h - bars[i - 1].c), Math.abs(b.l - bars[i - 1].c))));
}
const atr = (bars, n) => rma(trueRange(bars), n);

function rsiOf(x, n) {
  const up = x.map((v, i) => (i === 0 ? N : Math.max(v - x[i - 1], 0)));
  const dn = x.map((v, i) => (i === 0 ? N : Math.max(x[i - 1] - v, 0)));
  const au = rma(up, n), ad = rma(dn, n);
  return au.map((u, i) => (Number.isNaN(u) ? N : ad[i] === 0 ? 100 : 100 - 100 / (1 + u / ad[i])));
}

function stochOf(close, high, low, n) {
  const hh = highest(high, n), ll = lowest(low, n);
  return close.map((c, i) => (hh[i] === ll[i] ? 50 : 100 * (c - ll[i]) / (hh[i] - ll[i])));
}

const shift = (x, k) => (k >= 0 ? [...new Array(k).fill(N), ...x] : [...x.slice(-k), ...new Array(-k).fill(N)]);

// ── Parameter helpers ──
const P = (k, label, def, extra = {}) => ({ k, label, def, min: 1, step: 1, ...extra });
const SRC = { k: 'source', label: 'Source', def: 'close', source: SOURCES };
const L = (k, label, color, kind = 'line') => ({ k, label, color, kind });

export const INDICATORS = {
  // ── On the price chart ──
  sma: { name: 'Simple Moving Average', short: 'SMA', pane: 'price', params: [P('n', 'Length', 20), SRC],
         outputs: [L('v', 'SMA', '#2563EB')], calc: (b, p) => ({ v: sma(src(b, p.source), p.n) }) },
  ema: { name: 'Exponential Moving Average', short: 'EMA', pane: 'price', params: [P('n', 'Length', 20), SRC],
         outputs: [L('v', 'EMA', '#F59E0B')], calc: (b, p) => ({ v: ema(src(b, p.source), p.n) }) },
  wma: { name: 'Weighted Moving Average', short: 'WMA', pane: 'price', params: [P('n', 'Length', 20), SRC],
         outputs: [L('v', 'WMA', '#0891B2')], calc: (b, p) => ({ v: wma(src(b, p.source), p.n) }) },
  vwma: { name: 'Volume-Weighted Moving Average', short: 'VWMA', pane: 'price', params: [P('n', 'Length', 20), SRC],
          outputs: [L('v', 'VWMA', '#DB2777')],
          calc: (b, p) => { const x = src(b, p.source), pv = sma(x.map((v, i) => v * b[i].v), p.n), vv = sma(b.map(k => k.v), p.n);
                            return { v: pv.map((v, i) => (vv[i] ? v / vv[i] : N)) }; } },
  bb: { name: 'Bollinger Bands', short: 'BB', pane: 'price', params: [P('n', 'Length', 20), P('k', 'Std dev', 2, { step: 0.1, min: 0.1 }), SRC],
        outputs: [L('up', 'Upper', '#2563EB'), L('mid', 'Basis', '#F59E0B'), L('lo', 'Lower', '#2563EB')], fills: [['up', 'lo', '#2563EB']],
        calc: (b, p) => { const x = src(b, p.source), m = sma(x, p.n), sd = stdev(x, p.n, m);
                          return { up: m.map((v, i) => v + p.k * sd[i]), mid: m, lo: m.map((v, i) => v - p.k * sd[i]) }; } },
  keltner: { name: 'Keltner Channels', short: 'KC', pane: 'price', params: [P('n', 'EMA length', 20), P('a', 'ATR length', 10), P('m', 'Multiplier', 2, { step: 0.1, min: 0.1 })],
             outputs: [L('up', 'Upper', '#7C3AED'), L('mid', 'Basis', '#7C3AED'), L('lo', 'Lower', '#7C3AED')], fills: [['up', 'lo', '#7C3AED']],
             calc: (b, p) => { const m = ema(src(b, 'close'), p.n), r = atr(b, p.a);
                               return { up: m.map((v, i) => v + p.m * r[i]), mid: m, lo: m.map((v, i) => v - p.m * r[i]) }; } },
  donchian: { name: 'Donchian Channels', short: 'DC', pane: 'price', params: [P('n', 'Length', 20)],
              outputs: [L('up', 'Upper', '#0891B2'), L('mid', 'Basis', '#F59E0B'), L('lo', 'Lower', '#0891B2')], fills: [['up', 'lo', '#0891B2']],
              calc: (b, p) => { const up = highest(b.map(k => k.h), p.n), lo = lowest(b.map(k => k.l), p.n);
                                return { up, lo, mid: up.map((v, i) => (v + lo[i]) / 2) }; } },
  env: { name: 'Envelopes', short: 'ENV', pane: 'price', params: [P('n', 'Length', 20), P('pct', 'Percent', 2.5, { step: 0.1, min: 0.1 }), SRC],
         outputs: [L('up', 'Upper', '#16A34A'), L('mid', 'Basis', '#F59E0B'), L('lo', 'Lower', '#DC2626')],
         calc: (b, p) => { const m = sma(src(b, p.source), p.n);
                           return { up: m.map(v => v * (1 + p.pct / 100)), mid: m, lo: m.map(v => v * (1 - p.pct / 100)) }; } },
  psar: { name: 'Parabolic SAR', short: 'SAR', pane: 'price', params: [P('step', 'Step', 0.02, { step: 0.01, min: 0.001 }), P('max', 'Maximum', 0.2, { step: 0.01, min: 0.01 })],
          outputs: [L('v', 'SAR', '#7C3AED', 'dots')],
          calc: (b, p) => {
            const out = new Array(b.length).fill(N);
            if (b.length < 2) return { v: out };
            let up = b[1].c >= b[0].c, af = p.step, ep = up ? b[0].h : b[0].l, sar = up ? b[0].l : b[0].h;
            for (let i = 1; i < b.length; i++) {
              sar = sar + af * (ep - sar);
              if (up) {
                sar = Math.min(sar, b[i - 1].l, i > 1 ? b[i - 2].l : b[i - 1].l);
                if (b[i].l < sar) { up = false; sar = ep; ep = b[i].l; af = p.step; }
                else if (b[i].h > ep) { ep = b[i].h; af = Math.min(af + p.step, p.max); }
              } else {
                sar = Math.max(sar, b[i - 1].h, i > 1 ? b[i - 2].h : b[i - 1].h);
                if (b[i].h > sar) { up = true; sar = ep; ep = b[i].h; af = p.step; }
                else if (b[i].l < ep) { ep = b[i].l; af = Math.min(af + p.step, p.max); }
              }
              out[i] = sar;
            }
            return { v: out };
          } },
  supertrend: { name: 'SuperTrend', short: 'ST', pane: 'price', params: [P('a', 'ATR length', 10), P('m', 'Multiplier', 3, { step: 0.1, min: 0.1 })],
                outputs: [L('up', 'Up trend', '#16A34A'), L('dn', 'Down trend', '#DC2626')],
                calc: (b, p) => {
                  const r = atr(b, p.a), up = new Array(b.length).fill(N), dn = new Array(b.length).fill(N);
                  let fu = N, fl = N, trendUp = true;
                  for (let i = 0; i < b.length; i++) {
                    if (Number.isNaN(r[i])) continue;
                    const mid = (b[i].h + b[i].l) / 2, bu = mid + p.m * r[i], bl = mid - p.m * r[i];
                    const pc = i ? b[i - 1].c : b[i].c;
                    fu = Number.isNaN(fu) || bu < fu || pc > fu ? bu : fu;
                    fl = Number.isNaN(fl) || bl > fl || pc < fl ? bl : fl;
                    if (trendUp && b[i].c < fl) trendUp = false;
                    else if (!trendUp && b[i].c > fu) trendUp = true;
                    if (trendUp) up[i] = fl; else dn[i] = fu;
                  }
                  return { up, dn };
                } },
  ichimoku: { name: 'Ichimoku Cloud', short: 'Ichimoku', pane: 'price', params: [P('t', 'Conversion', 9), P('k', 'Base', 26), P('s', 'Span B', 52), P('d', 'Displacement', 26)],
              outputs: [L('tenkan', 'Conversion', '#2563EB'), L('kijun', 'Base', '#DC2626'), L('a', 'Span A', '#16A34A'), L('b', 'Span B', '#DC2626'), L('lag', 'Lagging', '#7C3AED')],
              fills: [['a', 'b', '#16A34A']],
              calc: (b, p) => {
                const h = b.map(k => k.h), l = b.map(k => k.l);
                const mid = n => { const hh = highest(h, n), ll = lowest(l, n); return hh.map((v, i) => (v + ll[i]) / 2); };
                const tenkan = mid(p.t), kijun = mid(p.k), sb = mid(p.s);
                return { tenkan, kijun, a: shift(tenkan.map((v, i) => (v + kijun[i]) / 2), p.d - 1), b: shift(sb, p.d - 1),
                         lag: shift(b.map(k => k.c), -(p.d - 1)) };
              } },

  // ── Own pane ──
  rsi: { name: 'Relative Strength Index', short: 'RSI', pane: 'own', range: [0, 100], levels: [30, 70], params: [P('n', 'Length', 14), SRC],
         outputs: [L('v', 'RSI', '#7C3AED')], calc: (b, p) => ({ v: rsiOf(src(b, p.source), p.n) }) },
  macd: { name: 'MACD', short: 'MACD', pane: 'own', levels: [0], params: [P('f', 'Fast', 12), P('s', 'Slow', 26), P('sig', 'Signal', 9), SRC],
          outputs: [L('hist', 'Histogram', '#94A3B8', 'hist'), L('macd', 'MACD', '#2563EB'), L('signal', 'Signal', '#F59E0B')],
          calc: (b, p) => { const x = src(b, p.source), m = sub(ema(x, p.f), ema(x, p.s)), s = ema(m, p.sig);
                            return { macd: m, signal: s, hist: sub(m, s) }; } },
  stoch: { name: 'Stochastic', short: 'Stoch', pane: 'own', range: [0, 100], levels: [20, 80], params: [P('n', '%K length', 14), P('ks', '%K smoothing', 3), P('d', '%D smoothing', 3)],
           outputs: [L('k', '%K', '#2563EB'), L('d', '%D', '#F59E0B')],
           calc: (b, p) => { const k = sma(stochOf(b.map(x => x.c), b.map(x => x.h), b.map(x => x.l), p.n), p.ks); return { k, d: sma(k, p.d) }; } },
  stochrsi: { name: 'Stochastic RSI', short: 'StochRSI', pane: 'own', range: [0, 100], levels: [20, 80], params: [P('r', 'RSI length', 14), P('n', 'Stoch length', 14), P('ks', '%K smoothing', 3), P('d', '%D smoothing', 3), SRC],
              outputs: [L('k', '%K', '#2563EB'), L('d', '%D', '#F59E0B')],
              calc: (b, p) => { const r = rsiOf(src(b, p.source), p.r), k = sma(stochOf(r, r, r, p.n), p.ks); return { k, d: sma(k, p.d) }; } },
  atr: { name: 'Average True Range', short: 'ATR', pane: 'own', params: [P('n', 'Length', 14)],
         outputs: [L('v', 'ATR', '#DC2626')], calc: (b, p) => ({ v: atr(b, p.n) }) },
  adx: { name: 'ADX / DMI', short: 'ADX', pane: 'own', levels: [20], params: [P('n', 'DI length', 14), P('s', 'ADX smoothing', 14)],
         outputs: [L('adx', 'ADX', '#7C3AED'), L('pdi', '+DI', '#16A34A'), L('mdi', '-DI', '#DC2626')],
         calc: (b, p) => {
           const pdm = b.map((k, i) => { if (!i) return N; const u = k.h - b[i - 1].h, d = b[i - 1].l - k.l; return u > d && u > 0 ? u : 0; });
           const mdm = b.map((k, i) => { if (!i) return N; const u = k.h - b[i - 1].h, d = b[i - 1].l - k.l; return d > u && d > 0 ? d : 0; });
           const tr = rma(trueRange(b).map((v, i) => (i ? v : N)), p.n);
           const pdi = rma(pdm, p.n).map((v, i) => 100 * v / tr[i]), mdi = rma(mdm, p.n).map((v, i) => 100 * v / tr[i]);
           const dx = pdi.map((v, i) => (v + mdi[i] ? 100 * Math.abs(v - mdi[i]) / (v + mdi[i]) : 0));
           return { adx: rma(dx, p.s), pdi, mdi };
         } },
  cci: { name: 'Commodity Channel Index', short: 'CCI', pane: 'own', levels: [-100, 100], params: [P('n', 'Length', 20)],
         outputs: [L('v', 'CCI', '#0891B2')],
         calc: (b, p) => { const tp = src(b, 'hlc3'), m = sma(tp, p.n);
                           return { v: tp.map((v, i) => { if (Number.isNaN(m[i])) return N; let md = 0; for (let k = 0; k < p.n; k++) md += Math.abs(tp[i - k] - m[i]); md /= p.n; return md ? (v - m[i]) / (0.015 * md) : 0; }) }; } },
  willr: { name: 'Williams %R', short: '%R', pane: 'own', range: [-100, 0], levels: [-80, -20], params: [P('n', 'Length', 14)],
           outputs: [L('v', '%R', '#7C3AED')],
           calc: (b, p) => ({ v: stochOf(b.map(x => x.c), b.map(x => x.h), b.map(x => x.l), p.n).map(v => v - 100) }) },
  obv: { name: 'On Balance Volume', short: 'OBV', pane: 'own', params: [],
         outputs: [L('v', 'OBV', '#2563EB')],
         calc: b => { let t = 0; return { v: b.map((k, i) => (t += i === 0 ? 0 : k.c > b[i - 1].c ? k.v : k.c < b[i - 1].c ? -k.v : 0)) }; } },
  mfi: { name: 'Money Flow Index', short: 'MFI', pane: 'own', range: [0, 100], levels: [20, 80], params: [P('n', 'Length', 14)],
         outputs: [L('v', 'MFI', '#16A34A')],
         calc: (b, p) => {
           const tp = src(b, 'hlc3'), pos = tp.map((v, i) => (i && v > tp[i - 1] ? v * b[i].v : 0)), neg = tp.map((v, i) => (i && v < tp[i - 1] ? v * b[i].v : 0));
           const sp = sma(pos, p.n), sn = sma(neg, p.n);
           return { v: sp.map((v, i) => (Number.isNaN(v) ? N : sn[i] === 0 ? 100 : 100 - 100 / (1 + v / sn[i]))) };
         } },
  roc: { name: 'Rate of Change', short: 'ROC', pane: 'own', levels: [0], params: [P('n', 'Length', 12), SRC],
         outputs: [L('v', 'ROC %', '#0891B2')],
         calc: (b, p) => { const x = src(b, p.source); return { v: x.map((v, i) => (i < p.n ? N : 100 * (v - x[i - p.n]) / x[i - p.n])) }; } },
  mom: { name: 'Momentum', short: 'MOM', pane: 'own', levels: [0], params: [P('n', 'Length', 10), SRC],
         outputs: [L('v', 'Momentum', '#DB2777')],
         calc: (b, p) => { const x = src(b, p.source); return { v: x.map((v, i) => (i < p.n ? N : v - x[i - p.n])) }; } },
  cmf: { name: 'Chaikin Money Flow', short: 'CMF', pane: 'own', levels: [0], params: [P('n', 'Length', 20)],
         outputs: [L('v', 'CMF', '#16A34A')],
         calc: (b, p) => { const mfv = b.map(k => (k.h === k.l ? 0 : ((k.c - k.l) - (k.h - k.c)) / (k.h - k.l) * k.v));
                           const a = sma(mfv, p.n), v = sma(b.map(k => k.v), p.n); return { v: a.map((x, i) => (v[i] ? x / v[i] : N)) }; } },
  aroon: { name: 'Aroon', short: 'Aroon', pane: 'own', range: [0, 100], params: [P('n', 'Length', 25)],
           outputs: [L('up', 'Up', '#16A34A'), L('dn', 'Down', '#DC2626')],
           calc: (b, p) => {
             const up = new Array(b.length).fill(N), dn = new Array(b.length).fill(N);
             for (let i = p.n; i < b.length; i++) {
               let hi = i, lo = i;
               for (let k = i - p.n; k <= i; k++) { if (b[k].h >= b[hi].h) hi = k; if (b[k].l <= b[lo].l) lo = k; }
               up[i] = 100 * (p.n - (i - hi)) / p.n; dn[i] = 100 * (p.n - (i - lo)) / p.n;
             }
             return { up, dn };
           } },
  stddev: { name: 'Standard Deviation', short: 'StdDev', pane: 'own', params: [P('n', 'Length', 20), SRC],
            outputs: [L('v', 'StdDev', '#F59E0B')], calc: (b, p) => ({ v: stdev(src(b, p.source), p.n) }) },
  ao: { name: 'Awesome Oscillator', short: 'AO', pane: 'own', levels: [0], params: [P('f', 'Fast', 5), P('s', 'Slow', 34)],
        outputs: [L('v', 'AO', '#16A34A', 'hist')], calc: (b, p) => { const m = src(b, 'hl2'); return { v: sub(sma(m, p.f), sma(m, p.s)) }; } },
  trix: { name: 'TRIX', short: 'TRIX', pane: 'own', levels: [0], params: [P('n', 'Length', 15), P('sig', 'Signal', 9)],
          outputs: [L('v', 'TRIX', '#DC2626'), L('signal', 'Signal', '#2563EB')],
          calc: (b, p) => { const e = ema(ema(ema(src(b, 'close'), p.n), p.n), p.n);
                            const v = e.map((x, i) => (i && !Number.isNaN(e[i - 1]) ? 100 * (x / e[i - 1] - 1) : N)); return { v, signal: ema(v, p.sig) }; } },
};

// New indicator instance with default params and styles.
let seq = 0;
export function newStudy(id, params = null, styles = null) {
  const def = INDICATORS[id];
  return {
    key: `s${Date.now().toString(36)}${(seq++).toString(36)}`, id, visible: true,
    params: params || Object.fromEntries(def.params.map(p => [p.k, p.def])),
    styles: styles || Object.fromEntries(def.outputs.map(o => [o.k, { color: o.color, width: o.kind === 'hist' ? 1 : 1.5 }])),
    fillOpacity: 0.08,
  };
}

export const studyLabel = s => {
  const def = INDICATORS[s.id];
  const vals = def.params.filter(p => !p.source).map(p => s.params[p.k]);
  return `${def.short}${vals.length ? ' ' + vals.join(', ') : ''}`;
};
